import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import moment from "moment";
import { getExitedEmployees } from "../../http/requests/settings";
import TableLoader from "../Loaders/TableLoader";

const monthOptions = [
  { value: "", label: "All months (year)" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function buildYearChoices() {
  const y = new Date().getFullYear();
  const list = [];
  for (let i = 0; i < 12; i++) {
    list.push(y - i);
  }
  return list;
}

const formatDate = (v) => {
  if (v == null || v === "") return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD-MM-YYYY") : String(v);
};

const formatDateCsv = (v) => {
  if (v == null || v === "") return "";
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD") : String(v);
};

function pick(r, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const val = r[k];
    if (val != null && val !== "") return val;
  }
  return "";
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildExitedCsv(rows) {
  const headers = [
    "Name",
    "Employee ID",
    "Email",
    "Department",
    "Stream",
    "Designation",
    "Date of joining",
    "Date of exit",
    "Total experience",
  ];
  const lines = [headers.map(csvEscape).join(",")];
  rows.forEach((r) => {
    const line = [
      pick(r, "name", "Name"),
      pick(r, "employeeId", "EmployeeId", "employeeID"),
      pick(r, "email", "Email"),
      pick(r, "department", "Department"),
      pick(r, "stream", "Stream"),
      pick(r, "designation", "Designation"),
      formatDateCsv(pick(r, "dateofjoining", "Dateofjoining")),
      formatDateCsv(pick(r, "dateOfExit", "DateOfExit")),
      pick(r, "totalExperience", "TotalExperience"),
    ].map(csvEscape);
    lines.push(line.join(","));
  });
  return `\uFEFF${lines.join("\r\n")}`;
}

function triggerDownload(csvText, filename) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Grid + filters for employees with Zoho DateOfExit in the selected range. */
export function ExitedEmployeesTable() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const yearChoices = useMemo(() => buildYearChoices(), []);

  const monthLabelForFile = useMemo(() => {
    if (month === "") return "all-months";
    const opt = monthOptions.find((o) => o.value === month);
    return opt ? opt.label.replace(/\s+/g, "-") : `month-${month}`;
  }, [month]);

  const handleDownloadReport = useCallback(() => {
    if (!rows.length) return;
    const stamp = moment().format("YYYYMMDD-HHmmss");
    const filename = `exited-employees-${year}-${monthLabelForFile}-${stamp}.csv`;
    triggerDownload(buildExitedCsv(rows), filename);
  }, [rows, year, monthLabelForFile]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const m = month === "" ? undefined : Number(month);
      const data = await getExitedEmployees(year, m);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
      const body = e?.data;
      const msg =
        (typeof body?.errorMessage === "string" && body.errorMessage) ||
        (typeof body?.ErrorMessage === "string" && body.ErrorMessage) ||
        e?.message ||
        "Could not load exited employees.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", mt: 2 }}>
      <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", borderBottom: "1px solid #e5e7eb" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1773bc" }}>
          Exit date range (Zoho)
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="exited-year">Year</InputLabel>
          <Select labelId="exited-year" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearChoices.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="exited-month">Month</InputLabel>
          <Select labelId="exited-month" label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
            {monthOptions.map((opt) => (
              <MenuItem key={opt.value === "" ? "all" : opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Count: {loading ? "…" : rows.length}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={rows.length ? "Download CSV for the current filters" : "Load data first"}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownloadReport}
              disabled={loading || !rows.length}
              sx={{ borderColor: "#1773bc", color: "#1773bc", whiteSpace: "nowrap" }}
            >
              Download report
            </Button>
          </span>
        </Tooltip>
      </Box>
      {error ? (
        <Typography color="error" sx={{ p: 2 }}>
          {error}
        </Typography>
      ) : null}
      <TableContainer sx={{ maxHeight: "48rem" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {["Name", "Employee ID", "Email", "Department", "Stream", "Designation", "Date of joining", "Date of exit", "Total experience"].map(
                (h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, backgroundColor: "#1773bc0d", color: "#1773bc", whiteSpace: "nowrap" }}
                  >
                    {h}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <TableLoader />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, idx) => (
                <TableRow key={`${r.email || ""}-${idx}`} hover>
                  <TableCell>{r.name ?? r.Name}</TableCell>
                  <TableCell>{r.employeeId ?? r.EmployeeId ?? r.employeeID ?? "—"}</TableCell>
                  <TableCell>{r.email ?? r.Email}</TableCell>
                  <TableCell>{r.department ?? r.Department ?? "—"}</TableCell>
                  <TableCell>{r.stream ?? r.Stream ?? "—"}</TableCell>
                  <TableCell>{r.designation ?? r.Designation ?? "—"}</TableCell>
                  <TableCell>{formatDate(r.dateofjoining ?? r.Dateofjoining)}</TableCell>
                  <TableCell>{formatDate(r.dateOfExit ?? r.DateOfExit)}</TableCell>
                  <TableCell>{r.totalExperience ?? r.TotalExperience ?? "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
